import { Button } from "@/shared/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { useEffect, useMemo, useState } from "react";
import type { FacebookResourceTargetDateRow } from "../types/preferences_types";

export function FacebookResourceTargetDateDialog({
    open,
    onOpenChange,
    mode,
    initial,
    errorMsg,
    onClearError,
    onSubmit,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    mode: "create" | "edit";
    initial?: FacebookResourceTargetDateRow;
    errorMsg?: string;
    onClearError?: () => void;
    onSubmit: (payload: { adAccountId: string; targetDate: string }) => void;
}) {
    const title = useMemo(
        () => `페이스북 리소스 수집 시 과거 데이터 비교일자 ${mode === "create" ? "추가" : "수정"}`,
        [mode]
    );

    const [adAccountId, setAdAccountId] = useState("");
    const [targetDate, setTargetDate] = useState("");

    useEffect(() => {
        if (!open) return;
        onClearError?.();

        if (mode === "edit" && initial) {
            setAdAccountId(initial.adAccountId);
            setTargetDate(initial.targetDate);
            return;
        }
        setAdAccountId("");
        setTargetDate("");
    }, [open, mode, initial, onClearError]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="space-y-2">
                        <div className="text-sm font-medium">ad_account_id</div>
                        <Input
                            value={adAccountId}
                            onChange={(e) => {
                                onClearError?.();
                                setAdAccountId(e.target.value);
                            }}
                            disabled={mode === "edit"}
                            maxLength={64}
                        />
                        <div className="text-xs text-muted-foreground">
                            과거 데이터 비교일자를 지정할 페이스북 계정의 ad_account_id
                        </div>
                    </div>

                    <div className="space-y-2">
                        <div className="text-sm font-medium">target_date</div>
                        <Input
                            value={targetDate}
                            onChange={(e) => {
                                onClearError?.();
                                setTargetDate(e.target.value);
                            }}
                            placeholder="YYYY-MM-DD"
                            maxLength={16}
                        />
                        <div className="text-xs text-muted-foreground">비교 대상이 될 날짜 (YYYY-MM-DD 형식)</div>
                    </div>

                    {errorMsg && (
                        <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
                            {errorMsg}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>
                        취소
                    </Button>
                    <Button
                        onClick={() => onSubmit({ adAccountId: adAccountId.trim(), targetDate: targetDate.trim() })}
                        disabled={!adAccountId.trim() || !targetDate.trim()}
                    >
                        {mode === "create" ? "추가" : "수정"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}